import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputLabel,
  TextField,
} from '@mui/material';
import { memo, useEffect } from 'react';
import { CloseIcon } from '~/assets/icons';
import { useFormik } from 'formik';
import { DIALOG_MODE_CREATE, DIALOG_MODE_EDIT } from '~/utils/constants/common';
import { cast, handleApiError, notify } from '~/utils/helper';
import { BaseDatePicker, BaseRow, BaseSelect } from '~/components/common';
import { useTranslation } from 'react-i18next';

import { useAppDispatch, useAppSelector } from '~/redux/hooks';

import { ICogs } from '~/utils/interface/cogs';
import { handleCreateCogs, handleEditCogs } from '~/thunks/cogs/cogsThunk';
import { cogsActions } from '~/thunks/cogs/cogsSlice';
import dayjs from 'dayjs';
import { IJob } from '~/utils/interface/job';
import { NumericFormat } from 'react-number-format';

type EditCOGSDialogProps = {
  open: boolean;
  setOpen?: (open: boolean, needRefreshData?: boolean) => void;
  setOpenDrawer?: (open: boolean) => void;
  mode?: string;
  title?: string;
  data?: ICogs;
  updateData: () => void;
  jobData?: IJob;
};

const initialValues: ICogs = {
  staffTimesheet: [],
};

const EditCOGSDialog = (props: EditCOGSDialogProps) => {
  const { mode = DIALOG_MODE_CREATE, data } = props;
  const { timeClocksSelection } = useAppSelector((state) => state.timeClocks);

  const { t } = useTranslation(['admin/cogs']);
  const dispatch = useAppDispatch();
  const form = useFormik({
    validateOnChange: false,
    initialValues,
    onSubmit: async (values, { setFieldError }) => {
      if (mode === DIALOG_MODE_CREATE) {
        try {
          await dispatch(handleCreateCogs({ ...values, jobId: String(props?.jobData?.id) })).unwrap();
          dispatch(cogsActions.changeNeedRefreshData(true));
          notify('success', t('createSuccess', { ns: 'admin/common' }));
          handleSetOpen(false, true);
          if (props.updateData) props.updateData();
        } catch (e: unknown) {
          handleApiError(e, setFieldError);
        }
        return;
      }
      if (mode === DIALOG_MODE_EDIT) {
        try {
          await dispatch(
            handleEditCogs({ id: data?.id, params: { ...values, jobId: String(props?.jobData?.id) } })
          ).unwrap();
          dispatch(cogsActions.changeNeedRefreshData(true));
          notify('success', t('editSuccess', { ns: 'admin/common' }));
          handleSetOpen(false, true);
          if (props.updateData) props.updateData();
        } catch (e: unknown) {
          handleApiError(e, setFieldError);
        }
        return;
      }
    },
  });

  useEffect(() => {
    if (mode === DIALOG_MODE_EDIT) {
      form.setFieldValue('name', cast(data?.name));
      form.setFieldValue('amount', cast(data?.amount));
      form.setFieldValue('balance', cast(data?.balance));
      form.setFieldValue('expenseDate', cast(data?.expenseDate));
      form.setFieldValue('number', cast(data?.number));
      form.setFieldValue('split', cast(data?.split));
      form.setFieldValue('transactionType', cast(data?.transactionType));
      form.setFieldValue('staffTimesheet', cast(data?.staffTimesheet));

      form.setFieldValue('description', cast(data?.description));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, data]);

  const handleSetOpen = (open: boolean, needRefreshData?: boolean) => {
    form.resetForm();
    if (props.setOpen) {
      props.setOpen(open, needRefreshData);
    }
    if (props.setOpenDrawer) {
      props.setOpenDrawer(open);
    }
  };

  const handleCancel = () => {
    handleSetOpen(false);
    form.resetForm();
  };

  return (
    <Dialog open={props.open} onClose={() => handleSetOpen(false)} fullWidth maxWidth='md'>
      <DialogTitle>
        {mode === DIALOG_MODE_CREATE ? t('addNew') : t('edit')}
        <IconButton onClick={() => handleSetOpen(false)} className='absolute right-[10px] top-[10px]'>
          <CloseIcon className='w-[10px] h-[10px]' />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        <Box component='form'>
          <BaseRow className='gap-[20px] mb-[20px]'>
            <Box className='w-full'>
              <InputLabel>{t('properties.staffTimesheet')}</InputLabel>
              <BaseSelect
                name='staffTimesheet'
                value={form.values.staffTimesheet}
                options={timeClocksSelection}
                onChange={(e) => {
                  form.setFieldValue('staffTimesheet', e.target.value);
                }}
                error={Boolean(form.errors?.staffTimesheet)}
                multiple
              />
            </Box>
          </BaseRow>
          <BaseRow className='gap-[20px] mb-[20px]'>
            <Box className='w-full'>
              <InputLabel required={true}>{t('properties.expenseDate')}</InputLabel>
              <BaseDatePicker
                placeholder={t('properties.expenseDate')}
                value={form.values.expenseDate}
                onChange={(date) => {
                  if (date) {
                    const selectedDate = dayjs(date).format('MM/DD/YYYY');

                    form.setFieldValue('expenseDate', selectedDate);
                  }
                }}
                error={Boolean(form.errors?.expenseDate)}
                helperText={form.errors?.expenseDate}
              />
            </Box>
            <Box className='w-full'>
              <InputLabel>{t('properties.transactionType')}</InputLabel>
              <TextField
                name='transactionType'
                placeholder={t('properties.transactionType')}
                value={form.values?.transactionType}
                onChange={form.handleChange}
                error={Boolean(form.errors?.transactionType)}
                helperText={form.errors?.transactionType}
              />
            </Box>
          </BaseRow>
          <BaseRow className='gap-[20px] mb-[20px]'>
            <Box className='w-full'>
              <InputLabel>{t('properties.name')}</InputLabel>
              <TextField
                name='name'
                placeholder={t('properties.name')}
                value={form.values?.name}
                onChange={form.handleChange}
                error={Boolean(form.errors?.name)}
                helperText={form.errors?.name}
              />
            </Box>
            <Box className='w-full'>
              <InputLabel>{t('properties.split')}</InputLabel>
              <TextField
                name='split'
                placeholder={t('properties.split')}
                value={form.values?.split}
                onChange={form.handleChange}
                error={Boolean(form.errors?.split)}
                helperText={form.errors?.split}
              />
            </Box>
          </BaseRow>
          <BaseRow className='gap-[20px] mb-[20px]'>
            <Box className='w-full'>
              <InputLabel required={true}>{t('properties.amount')}</InputLabel>
              <NumericFormat
                customInput={TextField}
                name='amount'
                placeholder={t('properties.amount')}
                value={form.values.amount}
                onValueChange={(val) => form.setFieldValue('amount', val.floatValue)}
                thousandSeparator={true}
                prefix={'$'}
                error={Boolean(form.errors.amount)}
                helperText={form.errors.amount}
                fullWidth
                decimalScale={2}
              />
            </Box>
            <Box className='w-full'>
              <InputLabel required={true}>{t('properties.balance')}</InputLabel>
              <NumericFormat
                customInput={TextField}
                name='balance'
                placeholder={t('properties.balance')}
                value={form.values.balance}
                onValueChange={(val) => form.setFieldValue('balance', val.floatValue)}
                thousandSeparator={true}
                prefix={'$'}
                error={Boolean(form.errors.balance)}
                helperText={form.errors.balance}
                fullWidth
                decimalScale={2}
              />
            </Box>
          </BaseRow>
          <BaseRow className='gap-[20px] mb-[20px]'>
            <Box className='w-full'>
              <InputLabel>{t('properties.number')}</InputLabel>
              <TextField
                name='number'
                placeholder={t('properties.number')}
                value={form.values?.number}
                onChange={form.handleChange}
                error={Boolean(form.errors?.number)}
                helperText={form.errors?.number}
              />
            </Box>
            <Box className='w-full'>
              <InputLabel>{t('properties.description')}</InputLabel>
              <TextField
                name='description'
                placeholder={t('properties.description')}
                value={form.values?.description}
                onChange={form.handleChange}
                error={Boolean(form.errors?.description)}
                helperText={form.errors?.description}
              />
            </Box>
          </BaseRow>
        </Box>
      </DialogContent>

      <DialogActions>
        <Button color='secondary' variant='contained' onClick={handleCancel}>
          {t('cancel', { ns: 'admin/common' })}
        </Button>
        <Button color='primary' variant='contained' onClick={() => form.handleSubmit()}>
          {t('save', { ns: 'admin/common' })}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default memo(EditCOGSDialog);
